import { CheckCircle2 } from "lucide-react";
import { KINDS, PLATFORMS, TONE, fmt, fmtDate, fmtTime } from "./meta";

function KindTile({ k, count, active, onClick, removalDate }) {
  const t = TONE[k.key];
  return (
    <button type="button" onClick={onClick} data-testid={`bsp-tile-${k.key}`}
            className={`group rounded-lg border bg-white px-4 py-3 text-left transition-[border-color,box-shadow,transform] hover:-translate-y-px ${active ? `ring-1 ${t.ring}` : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />{k.label}</div>
      <div className={`font-heading text-2xl font-bold tabular-nums ${count ? t.text : "text-slate-300"}`} data-testid={`bsp-count-${k.key}`}>{fmt(count)}</div>
      <div className="truncate text-[11px] text-slate-500">{k.key === "remove" && removalDate ? `month end · ${fmtDate(removalDate)}` : k.hint}</div>
    </button>
  );
}

function PlatformTable({ byPlatform, sources }) {
  return (
    <table className="w-full text-xs" data-testid="bsp-platform-table">
      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400">
        <th className="py-1.5 pr-3 text-left font-semibold">Platform</th><th className="px-2 text-left font-semibold">Read</th>
        {KINDS.map((k) => <th key={k.key} className="px-2 text-right font-semibold">{k.key === "create_subscription" ? "New subs" : k.label.replace("Would be ", "")}</th>)}
        <th className="pl-2 text-right font-semibold">In sync</th></tr></thead>
      <tbody>{PLATFORMS.map((p) => {
        const s = byPlatform?.[p.key] || {}; const src = sources?.[p.key] || {};
        return (
          <tr key={p.key} className="border-t border-slate-100" data-testid={`bsp-platform-row-${p.key}`}>
            <td className="py-2 pr-3 font-medium text-slate-800">{p.label}</td>
            <td className="px-2 text-slate-500">{fmt(src.domains)} domains · {fmt(src.users)} ext · {fmt(src.dids)} DIDs{src.territories != null ? ` · ${src.territories} territories` : ""}</td>
            {KINDS.map((k) => <td key={k.key} className={`px-2 text-right tabular-nums ${s[k.key] ? TONE[k.key].text : "text-slate-300"}`}>{fmt(s[k.key])}</td>)}
            <td className="pl-2 text-right tabular-nums text-slate-500">{fmt(s.in_sync)}</td>
          </tr>);
      })}</tbody>
    </table>
  );
}

export function RunSummary({ run, tab, onTab }) {
  const s = run.summary || {}; const src = s.sources || {};
  const unbilled = Object.entries(s.not_billed_by_srv_code || {});
  return (
    <div className="space-y-3" data-testid="bsp-summary">
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {KINDS.map((k) => <KindTile key={k.key} k={k} count={s.totals?.[k.key]} active={tab === k.key} onClick={() => onTab(k.key)} removalDate={s.removals_run_on} />)}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
        <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span className="flex items-center gap-1 font-medium text-emerald-700" data-testid="bsp-in-sync"><CheckCircle2 className="h-3.5 w-3.5" />{fmt(s.totals?.in_sync)} items already in sync</span>
          <span data-testid="bsp-billing-source">Stax Bill: {fmt(src.tracked_items)} tracked items {run.params?.source === "live" ? "read live" : `from the mirror (last change ${fmtTime(src.mirror_last_change)})`} · {fmt(src.subscriptions)} subscriptions · {fmt(src.rules)} mapping rules</span>
          <span className="ml-auto">Finished {fmtTime(run.finished_at)} · {run.seconds}s</span>
        </div>
        <PlatformTable byPlatform={s.by_platform} sources={src} />
        {unbilled.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2 text-[11px] text-slate-500" data-testid="bsp-unbilled">
            No mapping rule matched (not billed, as in the scripts):
            {unbilled.map(([k, n]) => <span key={k} className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-slate-600">{k} × {n}</span>)}
          </div>)}
      </div>
    </div>
  );
}
